import { useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';

import {
  useUploadNotesParquetFileMutation,
  useUploadNotesS3PathMutation,
  useUploadPersonParquetFileMutation,
  useUploadPersonS3PathMutation,
  useUploadVisitParquetFileMutation,
  useUploadVisitS3PathMutation,
} from '../../api/createProjectApiSlice';
import { DATA_IMPORT_MODES } from '../../constants';
import {
  clearUploadedNotesParquetFileName,
  clearUploadedPersonParquetFileName,
  clearUploadedVistParquetFileName,
  setDatasetImportMode,
  setUploadedNotesParquetFileName,
  setUploadedPersonParquetFileName,
  setUploadedVisitParquetFileName,
  setNotesS3FilePath as setNotesS3FilePathState,
  setVisitS3FilePath as setVisitS3FilePathState,
  setPersonS3FilePath as setPersonS3FilePathState,
  clearNotesS3FilePath,
  clearPersonS3FilePath,
  clearVisitS3FilePath,
} from '../../state/createProjectSlice';
import {
  selectDatasetImportMode,
  selectNotesS3FilePath,
  selectPersonS3FilePath,
  selectUploadedNotesParquetFileName,
  selectUploadedPersonParquetFileName,
  selectUploadedVisitParquetFileName,
  selectVisitS3FilePath,
  selectWorkflowId,
} from '../../state/selector';

interface UseDatasetImportProps {
  setRequiredFieldsFilled: (fieldsFilled: boolean) => void;
}

interface UseDatasetImportResults {
  uploadedNotesParquetFileName: string | null;
  uploadedPersonParquetFileName: string | null;
  uploadedVisitParquetFileName: string | null;
  handleRemoveNotesFile: () => void;
  handleRemovePersonFile: () => void;
  handleRemoveVisitFile: () => void;
  handleNotesClick: () => void;
  handlePersonClick: () => void;
  handleVisitClick: () => void;
  handleNotesDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  handlePersonDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  handleVisitDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  isUploadNotesParquetFileLoading: boolean;
  isUploadPersonParquetFileLoading: boolean;
  isUploadVisitParquetFileLoading: boolean;
  inputNotesRef: React.RefObject<HTMLInputElement>;
  inputPersonRef: React.RefObject<HTMLInputElement>;
  inputVisitRef: React.RefObject<HTMLInputElement>;
  handleUploadNotesParquetFile: (file: File | null) => Promise<void>;
  handleUploadPersonParquetFile: (file: File | null) => Promise<void>;
  handleUploadVisitParquetFile: (file: File | null) => Promise<void>;
  uploadNotesError: string | null;
  uploadPersonError: string | null;
  uploadVisitError: string | null;
  datasetImportMode: string;
  setDataImportMode: (mode: (typeof DATA_IMPORT_MODES)[number]) => void;
  uploadedNotesS3FilePath: string | null;
  uploadedPersonS3FilePath: string | null;
  uploadedVisitS3FilePath: string | null;

  handleSetNotesS3Path: (s3Path: string) => Promise<void>;
  handleSetPersonS3Path: (s3Path: string) => Promise<void>;
  handleSetVisitS3Path: (s3Path: string) => Promise<void>;

  notesS3Error: string | null;
  personS3Error: string | null;
  visitS3Error: string | null;

  isSetNotesS3FilePathLoading: boolean;
  isSetPersonS3FilePathLoading: boolean;
  isSetVisitS3FilePathLoading: boolean;

  notesS3Input: string;
  personS3Input: string;
  visitS3Input: string;
  setNotesS3Input: (value: string) => void;
  setPersonS3Input: (value: string) => void;
  setVisitS3Input: (value: string) => void;

  handleRemoveNotesS3Path: () => void;
  handleRemovePersonS3Path: () => void;
  handleRemoveVisitS3Path: () => void;
}

export const useDatasetImport = ({
  setRequiredFieldsFilled,
}: UseDatasetImportProps): UseDatasetImportResults => {
  const datasetImportMode = useAppSelector(selectDatasetImportMode);

  const [uploadNotesParquetFile, { isLoading: isUploadNotesParquetFileLoading }] =
    useUploadNotesParquetFileMutation();
  const [uploadPersonParquetFile, { isLoading: isUploadPersonParquetFileLoading }] =
    useUploadPersonParquetFileMutation();
  const [uploadVisitParquetFile, { isLoading: isUploadVisitParquetFileLoading }] =
    useUploadVisitParquetFileMutation();

  const [setNotesS3FilePath, { isLoading: isSetNotesS3FilePathLoading }] =
    useUploadNotesS3PathMutation();
  const [setPersonS3FilePath, { isLoading: isSetPersonS3FilePathLoading }] =
    useUploadPersonS3PathMutation();
  const [setVisitS3FilePath, { isLoading: isSetVisitS3FilePathLoading }] =
    useUploadVisitS3PathMutation();

  const uploadedNotesParquetFileName = useAppSelector(selectUploadedNotesParquetFileName);
  const uploadedPersonParquetFileName = useAppSelector(selectUploadedPersonParquetFileName);
  const uploadedVisitParquetFileName = useAppSelector(selectUploadedVisitParquetFileName);

  const uploadedNotesS3FilePath = useAppSelector(selectNotesS3FilePath);
  const uploadedPersonS3FilePath = useAppSelector(selectPersonS3FilePath);
  const uploadedVisitS3FilePath = useAppSelector(selectVisitS3FilePath);

  const workflowId = useAppSelector(selectWorkflowId);

  const inputNotesRef = useRef<HTMLInputElement | null>(null);
  const inputPersonRef = useRef<HTMLInputElement | null>(null);
  const inputVisitRef = useRef<HTMLInputElement | null>(null);

  const [uploadNotesError, setUploadNotesError] = useState<string | null>(null);
  const [uploadPersonError, setUploadPersonError] = useState<string | null>(null);
  const [uploadVisitError, setUploadVisitError] = useState<string | null>(null);

  const [notesS3Error, setNotesS3Error] = useState<string | null>(null);
  const [personS3Error, setPersonS3Error] = useState<string | null>(null);
  const [visitS3Error, setVisitS3Error] = useState<string | null>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (datasetImportMode === DATA_IMPORT_MODES[0]) {
      // file-upload mode
      if (
        uploadedNotesParquetFileName &&
        uploadedPersonParquetFileName &&
        uploadedVisitParquetFileName
      ) {
        setRequiredFieldsFilled(true);
      } else {
        setRequiredFieldsFilled(false);
      }
    }

    if (datasetImportMode === DATA_IMPORT_MODES[1]) {
      // s3 mode
      if (uploadedNotesS3FilePath && uploadedPersonS3FilePath && uploadedVisitS3FilePath) {
        setRequiredFieldsFilled(true);
      } else {
        setRequiredFieldsFilled(false);
      }
    }
  }, [
    datasetImportMode,
    uploadedNotesParquetFileName,
    uploadedPersonParquetFileName,
    uploadedVisitParquetFileName,
    uploadedNotesS3FilePath,
    uploadedPersonS3FilePath,
    uploadedVisitS3FilePath,
    setRequiredFieldsFilled,
  ]);

  const [notesS3Input, setNotesS3Input] = useState('');
  const [personS3Input, setPersonS3Input] = useState('');
  const [visitS3Input, setVisitS3Input] = useState('');

  useEffect(() => {
    setNotesS3Input(uploadedNotesS3FilePath || '');
  }, [uploadedNotesS3FilePath]);

  useEffect(() => {
    setPersonS3Input(uploadedPersonS3FilePath || '');
  }, [uploadedPersonS3FilePath]);

  useEffect(() => {
    setVisitS3Input(uploadedVisitS3FilePath || '');
  }, [uploadedVisitS3FilePath]);

  const handleNotesClick = () => {
    inputNotesRef.current?.click();
    setUploadNotesError(null);
  };
  const handlePersonClick = () => {
    inputPersonRef.current?.click();
    setUploadPersonError(null);
  };
  const handleVisitClick = () => {
    inputVisitRef.current?.click();
    setUploadVisitError(null);
  };

  const handleUploadNotesParquetFile = async (file: File | null) => {
    if (!file) return;
    if (!workflowId) return;
    try {
      await uploadNotesParquetFile({ file, workflowId }).unwrap();
      dispatch(setUploadedNotesParquetFileName(file.name));
      setUploadNotesError(null);
    } catch (error) {
      setUploadNotesError(
        'Failed to upload file : ' + (error as { data: { message: string } }).data.message
      );
      console.error('Failed to upload file', error);
    }
  };
  const handleUploadPersonParquetFile = async (file: File | null) => {
    if (!file) return;
    if (!workflowId) return;
    try {
      await uploadPersonParquetFile({ file, workflowId }).unwrap();
      dispatch(setUploadedPersonParquetFileName(file.name));
      setUploadPersonError(null);
    } catch (error) {
      setUploadPersonError(
        'Failed to upload file : ' + (error as { data: { message: string } }).data.message
      );
      console.error('Failed to upload file', error);
    }
  };
  const handleUploadVisitParquetFile = async (file: File | null) => {
    if (!file) return;
    if (!workflowId) return;
    try {
      await uploadVisitParquetFile({ file, workflowId }).unwrap();
      dispatch(setUploadedVisitParquetFileName(file.name));
      setUploadVisitError(null);
    } catch (error) {
      setUploadNotesError(
        'Failed to upload file : ' + (error as { data: { message: string } }).data.message
      );
      console.error('Failed to upload file', error);
    }
  };

  const handleSetNotesS3Path = async (s3Path: string) => {
    if (!s3Path || !workflowId) return;
    try {
      await setNotesS3FilePath({ s3Path, workflowId }).unwrap();
      dispatch(setNotesS3FilePathState(s3Path));
      setNotesS3Error(null);
    } catch (error) {
      setNotesS3Error(
        'Failed to set S3 path : ' + (error as { data: { message: string } }).data.message
      );
    }
  };

  const handleSetPersonS3Path = async (s3Path: string) => {
    if (!s3Path || !workflowId) return;
    try {
      await setPersonS3FilePath({ s3Path, workflowId }).unwrap();
      dispatch(setPersonS3FilePathState(s3Path));
      setPersonS3Error(null);
    } catch (error) {
      setPersonS3Error(
        'Failed to set S3 path : ' + (error as { data: { message: string } }).data.message
      );
    }
  };

  const handleSetVisitS3Path = async (s3Path: string) => {
    if (!s3Path || !workflowId) return;
    try {
      await setVisitS3FilePath({ s3Path, workflowId }).unwrap();
      dispatch(setVisitS3FilePathState(s3Path));
      setVisitS3Error(null);
    } catch (error) {
      setVisitS3Error(
        'Failed to set S3 path : ' + (error as { data: { message: string } }).data.message
      );
    }
  };

  const handleNotesDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    if (!workflowId) return;
    try {
      await uploadNotesParquetFile({ file, workflowId }).unwrap();
      dispatch(setUploadedNotesParquetFileName(file.name));
      setUploadNotesError(null);
    } catch (error) {
      setUploadNotesError(
        'Failed to upload file : ' + (error as { data: { message: string } }).data.message
      );
      console.error('Failed to upload file', error);
    }
  };

  const handlePersonDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    if (!workflowId) return;
    try {
      await uploadPersonParquetFile({ file, workflowId }).unwrap();
      dispatch(setUploadedPersonParquetFileName(file.name));
      setUploadPersonError(null);
    } catch (error) {
      setUploadPersonError(
        'Failed to upload file : ' + (error as { data: { message: string } }).data.message
      );
      console.error('Failed to upload file', error);
    }
  };

  const handleVisitDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    if (!workflowId) return;
    try {
      await uploadVisitParquetFile({ file, workflowId }).unwrap();
      dispatch(setUploadedVisitParquetFileName(file.name));
      setUploadVisitError(null);
    } catch (error) {
      setUploadNotesError(
        'Failed to upload file : ' + (error as { data: { message: string } }).data.message
      );
      console.error('Failed to upload file', error);
    }
  };

  const handleRemoveNotesFile = () => {
    dispatch(clearUploadedNotesParquetFileName());
    setRequiredFieldsFilled(false);
    setUploadNotesError(null);
    if (inputNotesRef.current) {
      inputNotesRef.current.value = '';
    }
  };

  const handleRemovePersonFile = () => {
    dispatch(clearUploadedPersonParquetFileName());
    setRequiredFieldsFilled(false);
    setUploadPersonError(null);
    if (inputPersonRef.current) {
      inputPersonRef.current.value = '';
    }
  };

  const handleRemoveVisitFile = () => {
    dispatch(clearUploadedVistParquetFileName());
    setRequiredFieldsFilled(false);
    setUploadVisitError(null);
    if (inputVisitRef.current) {
      inputVisitRef.current.value = '';
    }
  };

  const handleRemoveNotesS3Path = () => {
    dispatch(clearNotesS3FilePath());
    setRequiredFieldsFilled(false);
    setNotesS3Error(null);
    if (inputNotesRef.current) {
      inputNotesRef.current.value = '';
    }
  };

  const handleRemovePersonS3Path = () => {
    dispatch(clearPersonS3FilePath());
    setRequiredFieldsFilled(false);
    setPersonS3Error(null);
    if (inputPersonRef.current) {
      inputPersonRef.current.value = '';
    }
  };

  const handleRemoveVisitS3Path = () => {
    dispatch(clearVisitS3FilePath());
    setRequiredFieldsFilled(false);
    setVisitS3Error(null);
    if (inputVisitRef.current) {
      inputVisitRef.current.value = '';
    }
  };

  const setDataImportMode = (mode: (typeof DATA_IMPORT_MODES)[number]) => {
    dispatch(setDatasetImportMode(mode));
  };

  return {
    uploadedNotesParquetFileName,
    uploadedPersonParquetFileName,
    uploadedVisitParquetFileName,
    handleRemoveNotesFile,
    handleRemovePersonFile,
    handleRemoveVisitFile,
    handleNotesClick,
    handlePersonClick,
    handleVisitClick,
    handleNotesDrop,
    handlePersonDrop,
    handleVisitDrop,
    isUploadNotesParquetFileLoading,
    isUploadPersonParquetFileLoading,
    isUploadVisitParquetFileLoading,
    inputNotesRef,
    inputPersonRef,
    inputVisitRef,
    handleUploadNotesParquetFile,
    handleUploadPersonParquetFile,
    handleUploadVisitParquetFile,
    uploadNotesError,
    uploadPersonError,
    uploadVisitError,
    datasetImportMode,
    setDataImportMode,
    uploadedNotesS3FilePath,
    uploadedPersonS3FilePath,
    uploadedVisitS3FilePath,

    handleSetNotesS3Path,
    handleSetPersonS3Path,
    handleSetVisitS3Path,

    notesS3Error,
    personS3Error,
    visitS3Error,

    isSetNotesS3FilePathLoading,
    isSetPersonS3FilePathLoading,
    isSetVisitS3FilePathLoading,

    notesS3Input,
    personS3Input,
    visitS3Input,
    setNotesS3Input,
    setPersonS3Input,
    setVisitS3Input,

    handleRemoveNotesS3Path,
    handleRemovePersonS3Path,
    handleRemoveVisitS3Path,
  };
};
